import { uniq } from 'lodash';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { useFlags } from '../components/FeatureFlags';
import {
  useAnalyticsID,
  useUser,
  useUserHipaa,
  useUserLocale,
} from '../remoteData/account';
import { getTrendsViewSettingsState } from '../selectors';
import { UserData } from './types';
import { booleanObjectToList } from './utils';

const SESSION_ID_KEY = 'AMP_SESSION_ID';
const LAST_EVENT_TS_KEY = 'LAST_EVENT_TS';
const TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Since we are not using the client-side integration for Amplitude, we don't
 * load the Amplitude SDK and thus need to track sessions ourselves:
 * https://segment.com/docs/destinations/amplitude/#sessionid
 *
 * By default, Amplitude uses a session ID that is the start time of the session
 * (as the ms since the epoch). On the web, the session time out is 30 minutes
 * by default:
 * https://help.amplitude.com/hc/en-us/articles/115002323627-Tracking-Sessions
 *
 * We'll store this session ID as well as the timestamp of the last event in
 * localStorage and invalidate the ID if the latest timestamp is over 30 minutes
 * since the previous one. If we can't store the session ID, we return -1.
 */
function getSessionID() {
  try {
    const currentTimestamp = Date.now();
    const currentTimestampString = currentTimestamp.toString();
    const previousSessionIDString = localStorage.getItem(SESSION_ID_KEY);
    const lastEventTimestampString = localStorage.getItem(LAST_EVENT_TS_KEY);
    const lastEventTimestamp =
      lastEventTimestampString != null
        ? Number.parseInt(lastEventTimestampString, 10)
        : undefined;
    const previousSessionId =
      previousSessionIDString != null
        ? Number.parseInt(previousSessionIDString, 10)
        : undefined;

    localStorage.setItem(LAST_EVENT_TS_KEY, currentTimestampString);

    if (
      !previousSessionId ||
      !lastEventTimestamp ||
      Number.isNaN(lastEventTimestamp) ||
      Number.isNaN(previousSessionId) ||
      currentTimestamp > lastEventTimestamp + TIMEOUT_MS
    ) {
      localStorage.setItem(SESSION_ID_KEY, currentTimestampString);
      return currentTimestamp;
    }

    return previousSessionId;
  } catch {
    // Likely no access to localStorage
    return -1;
  }
}

export function getCommonOptions(
  isHipaa: undefined | boolean,
  includeAppDetails = false,
) {
  const options: SegmentAnalytics.SegmentOpts = {
    integrations: {
      Amplitude: {
        session_id: getSessionID(),
      },
    },
    context: {
      traits: {
        isHipaa: !!isHipaa,
      },
      ...(includeAppDetails
        ? {
            app: {
              name: 'Oura Cloud',
              version: process.env.RELEASE_VERSION,
              build: process.env.RELEASE_VERSION,
            },
          }
        : {}),
    },
  };

  return options;
}

function backendFeaturesToList(userData?: UserData) {
  const features = [];

  if (userData?.features.blackout?.enabled) {
    features.push('blackout');
  }

  return features;
}

export function UserAnalytics() {
  const uid = useAnalyticsID();
  const { data: userData } = useUser();
  const gender = userData?.userInfo.gender;
  const age = userData?.userInfo.age;
  const isHipaa = userData?.isHipaa;
  const { query } = useSelector(getTrendsViewSettingsState);
  const { units } = useUserLocale();
  const featureFlags = useFlags();
  // Note: this assumes all feature flag values are booleans
  const enabledFeatureFlagsWeb = booleanObjectToList(featureFlags).sort();
  const enabledBackendFeatures = backendFeaturesToList(userData);

  const timeAggregation = query?.timeAggregation;
  const subqueries = query?.subqueries;

  useEffect(() => {
    if (window.analytics && uid) {
      const traits = {
        gender,
        age,
        units,
        trendsChartCount: subqueries?.length,
        trendsMetrics:
          subqueries &&
          uniq(subqueries.flatMap((s) => (s.y2 ? [s.y1, s.y2] : [s.y1]))),
        trendsMetricPairs: subqueries?.map((s) =>
          s.y2 ? [s.y1, s.y2].sort().join('/') : s.y1,
        ),
        trendsAggregation: timeAggregation,
        enabledFeatureFlagsWeb,
        enabledBackendFeatures,
        isHipaa,
      };
      window.analytics.identify(uid, traits, getCommonOptions(isHipaa, true));
    }
    // We disable this because of the stringified arrays, which the rule doesn't support
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [
    uid,
    timeAggregation,
    subqueries,
    units,
    gender,
    age,
    isHipaa,
    enabledFeatureFlagsWeb.join('-'),
    enabledBackendFeatures.join('-'),
  ]);
  /* eslint-enable react-hooks/exhaustive-deps */

  return null;
}

/**
 * Do not use this hook for anonymous/unauthenticated pages! It will trigger a call to the
 * backend and result in a redirect to the login page. Use useAnonymousAnalyticsTrackPage instead.
 */
export function useAnalyticsTrackPage({
  name,
  category,
  dependencies = [],
  properties = {},
}: {
  name: string;
  category: string;
  dependencies?: Array<string | number | boolean>; // We only want value types
  properties?: Record<string, unknown>;
}) {
  const isHipaa = useUserHipaa();
  const propertiesWithHipaa = {
    isHipaa: !!isHipaa,
    ...properties,
  };
  useEffect(() => {
    window.analytics?.page(
      category,
      name,
      propertiesWithHipaa,
      getCommonOptions(isHipaa),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies, isHipaa, name, category]);
}

export function useAnonymousAnalyticsTrackPage({
  name,
  category,
  dependencies = [],
  properties = {},
}: {
  name: string;
  category?: string;
  dependencies?: Array<string | number | boolean>; // We only want value types
  properties?: Record<string, unknown>;
}) {
  useEffect(() => {
    if (category) {
      window.analytics?.page(
        category,
        name,
        {
          isHipaa: false,
          ...properties,
        },
        getCommonOptions(undefined),
      );
    } else {
      window.analytics?.page(name, properties, getCommonOptions(undefined));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies, name, category]);
}

/**
 * Do not use this hook for anonymous/unauthenticated pages! It will trigger a call to the
 * backend and result in a redirect to the login page. Use useAnonymousAnalyticsTrackEvent instead.
 */
export function useAnalyticsTrackEvent() {
  const isHipaa = useUserHipaa();

  return function analyticsTrackEvent(
    event: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    properties?: Record<string, any>,
    callback?: () => void,
  ) {
    window.analytics?.track(
      event,
      { isHipaa: !!isHipaa, ...properties },
      getCommonOptions(isHipaa),
      callback,
    );
  };
}

export function useAnonymousAnalyticsTrackEvent() {
  return function analyticsTrackEvent(
    event: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    properties?: Record<string, any>,
    callback?: () => void,
  ) {
    window.analytics?.track(
      event,
      { isHipaa: false, ...properties },
      getCommonOptions(undefined),
      callback,
    );
  };
}

export function analyticsReset() {
  window.analytics?.reset();
}

export function isAnalyticsLoaded() {
  return !!window.analytics;
}
